import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight, ShieldCheck, RefreshCw } from "lucide-react";

export default function Login() {
  const { login, verify2FA, resend2FA } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // 2FA state
  const [challenge, setChallenge] = useState(null); // { challenge_id, email_masked }
  const [code, setCode] = useState("");
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(form.email, form.password);
    setBusy(false);
    if (!res.ok) return setError(res.error);
    if (res.twoFactor) {
      setChallenge({ challenge_id: res.challenge_id, email_masked: res.email_masked });
      return;
    }
    nav(res.user.role === "admin" ? "/admin" : "/portal");
  };

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return setError("Enter the 6-digit code from your email.");
    setBusy(true);
    setError("");
    const res = await verify2FA(challenge.challenge_id, code);
    setBusy(false);
    if (!res.ok) return setError(res.error);
    nav(res.user.role === "admin" ? "/admin" : "/portal");
  };

  const onResend = async () => {
    setResending(true);
    setResendNote("");
    setError("");
    const res = await resend2FA(challenge.challenge_id);
    setResending(false);
    if (res.ok) setResendNote("New code sent. Check your inbox.");
    else setError(res.error);
  };

  const cancel2FA = () => {
    setChallenge(null);
    setCode("");
    setError("");
    setResendNote("");
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-8">
          <img src="/brand/logo-white-512.png" alt="Coastal Bend Contractor Co." className="h-32 w-auto object-contain" />
        </Link>

        {!challenge ? (
          <div className="card p-8">
            <h1 className="font-display text-2xl lg:text-3xl font-bold mb-2">Welcome back.</h1>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Sign in to your contractor or client account.</p>

            <form onSubmit={submit} className="space-y-4">
              <label className="block">
                <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Email</div>
                <input type="email" className="input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" />
              </label>
              <label className="block">
                <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Password</div>
                <input type="password" className="input" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" />
              </label>
              {error && <div className="text-sm" style={{ color: '#fca5a5' }} data-testid="login-error">{error}</div>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit">
                {busy ? "Signing in…" : (<>Sign in <ArrowRight size={15} /></>)}
              </button>
            </form>

            <div className="divider my-6" />
            <div className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>
              New here? <Link to="/register" className="hover:underline" style={{ color: 'var(--accent)' }} data-testid="login-register-link">Create a client account</Link>
            </div>
          </div>
        ) : (
          <div className="card p-8" data-testid="2fa-card">
            <div className="flex items-center gap-2 mb-1" style={{ color: 'var(--accent)' }}>
              <ShieldCheck size={16} />
              <div className="text-xs uppercase tracking-[0.18em] font-bold">Two-factor sign-in</div>
            </div>
            <h1 className="font-display text-2xl lg:text-3xl font-bold mb-2">Check your email.</h1>
            <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
              We just sent a 6-digit code to <strong style={{ color: 'var(--text)' }}>{challenge.email_masked}</strong>. It expires in 10 minutes.
            </p>

            <form onSubmit={verify} className="space-y-4">
              <label className="block">
                <div className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: 'var(--text-muted)' }}>Code</div>
                <input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  className="input text-center font-mono tracking-[0.6em] text-xl !py-3"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  autoFocus
                  data-testid="2fa-code"
                />
              </label>
              {error && <div className="text-sm" style={{ color: '#fca5a5' }} data-testid="2fa-error">{error}</div>}
              {resendNote && <div className="text-sm" style={{ color: '#86efac' }} data-testid="2fa-resend-note">{resendNote}</div>}
              <button type="submit" disabled={busy || code.length !== 6} className="btn-primary w-full justify-center" data-testid="2fa-submit">
                {busy ? "Verifying…" : (<>Verify & sign in <ArrowRight size={15} /></>)}
              </button>
            </form>

            <div className="divider my-6" />
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={cancel2FA}
                className="hover:underline"
                style={{ color: 'var(--text-muted)' }}
                data-testid="2fa-cancel"
              >
                ← Use a different account
              </button>
              <button
                type="button"
                onClick={onResend}
                disabled={resending}
                className="flex items-center gap-1.5 hover:underline disabled:opacity-50"
                style={{ color: 'var(--accent)' }}
                data-testid="2fa-resend"
              >
                <RefreshCw size={13} className={resending ? "animate-spin" : ""} />
                {resending ? "Sending…" : "Resend code"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
